import CurrencyLogo from 'components/CurrencyLogo'
import { MouseoverTooltip } from 'components/Tooltip'
import { useHarvestRewards } from 'hooks/useHarvestRewards'
import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { ThemedText } from 'theme'
import { Token as PortfolioToken } from 'types/portfolio'
import { formatNumber } from 'utils/formatCurrencyAmount'

import { ClaimActionButton } from './ClaimActionButton'

const RewardTooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const RewardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 120px;
`

interface PendingReward {
  token: PortfolioToken
  amount: { toExact: () => string }
  amountUSD: number
}

interface ClaimRewardsButtonProps {
  pendingRewards: PendingReward[]
  showTooltip?: boolean
  onClaimSuccess?: () => void
}

export function ClaimRewardsButton({ pendingRewards, showTooltip = true, onClaimSuccess }: ClaimRewardsButtonProps) {
  const hasRewards = pendingRewards.length > 0
  const notifiedHarvestHashRef = useRef<string | null>(null)

  const { harvest, harvesting, harvestHash, isHarvestPending, canHarvest, isHarvested } = useHarvestRewards({
    hasRewards,
  })

  const handleClaim = async (event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    await harvest()
  }

  useEffect(() => {
    if (!onClaimSuccess || !harvestHash || !isHarvested) return
    if (notifiedHarvestHashRef.current === harvestHash) return

    notifiedHarvestHashRef.current = harvestHash
    onClaimSuccess()
  }, [harvestHash, isHarvested, onClaimSuccess])

  const tooltipContent =
    showTooltip && pendingRewards.length > 0 ? (
      <RewardTooltipContent>
        {pendingRewards.map((reward, index) => (
          <RewardRow key={index}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CurrencyLogo logoURI={reward.token.logoUri} size="16px" className="mr-1.5" />
              <ThemedText.DeprecatedMain fontSize="12px">{reward.token.symbol}</ThemedText.DeprecatedMain>
            </div>
            <ThemedText.DeprecatedMain fontSize="12px">
              {formatNumber(parseFloat(reward.amount.toExact()))}
            </ThemedText.DeprecatedMain>
          </RewardRow>
        ))}
      </RewardTooltipContent>
    ) : null

  if (!hasRewards) {
    return null
  }

  const button = (
    <ClaimActionButton
      disabled={harvesting || !!harvestHash || !canHarvest}
      isClaiming={isHarvestPending || harvesting}
      isClaimed={isHarvested}
      onClick={handleClaim}
    />
  )

  if (!showTooltip || !tooltipContent) {
    return button
  }

  return <MouseoverTooltip text={tooltipContent}>{button}</MouseoverTooltip>
}
